/**
 * FP's installment_day contract for mutual-fund transaction plans.
 *
 * Ported verbatim from the user platform's `helpers/mfSchedule.ts`. It is the
 * single place that knows what `installment_day` MEANS, which is not the same
 * thing at every frequency:
 *
 *   daily                              no day at all
 *   day_in_a_week / day_in_a_fortnight a WEEKDAY, 1-5 (Monday-Friday)
 *   twice/four times a month, monthly,
 *   quarterly, half-yearly, yearly     a DAY OF THE MONTH, 1-28
 *
 * Before this was ported the admin treated every non-daily frequency as a
 * day-of-month, so a weekly SIP went to the gateway with a month day where a
 * weekday was expected. Read allowed days through `getAllowedMFInstallmentDays`
 * and label them through `formatMFInstallmentDay` rather than assuming either.
 *
 * `normalizeMFFrequency` also folds the synonyms the API uses interchangeably
 * (weekly, fortnightly, annual) onto the canonical names.
 */

export type MFScheduleDayMode = "none" | "weekday" | "month_day";

export interface MFFrequencyScheduleRule {
  dayMode: MFScheduleDayMode;
  allowedDays: number[];
}

const range = (start: number, end: number) =>
  Array.from({ length: end - start + 1 }, (_, index) => start + index);

const WEEKDAYS = range(1, 5);
const MONTH_DAYS = range(1, 28);

export const normalizeMFFrequency = (frequency?: string | null) => {
  const normalized = (frequency || "").trim().toLowerCase().replace(/-/g, "_");
  if (normalized === "weekly") return "day_in_a_week";
  if (normalized === "fortnightly") return "day_in_a_fortnight";
  if (normalized === "annual") return "yearly";
  return normalized;
};

/** FP's global installment_day contract for transaction plans. */
export const getMFFrequencyScheduleRule = (
  frequency?: string | null,
): MFFrequencyScheduleRule | null => {
  switch (normalizeMFFrequency(frequency)) {
    case "daily":
      return { dayMode: "none", allowedDays: [] };
    case "day_in_a_week":
    case "day_in_a_fortnight":
      return { dayMode: "weekday", allowedDays: WEEKDAYS };
    case "four_times_a_month":
    case "twice_a_month":
    case "monthly":
    case "quarterly":
    case "half_yearly":
    case "yearly":
      return { dayMode: "month_day", allowedDays: MONTH_DAYS };
    default:
      return null;
  }
};

/**
 * Applies FP's limits to scheme days. A valid subset is preserved because a
 * scheme may support fewer days. If staging returns only invalid values, the
 * FP range is used as the safe source-of-truth fallback.
 */
export const getAllowedMFInstallmentDays = (
  frequency: string,
  schemeDays: number[] = [],
) => {
  const fpRule = getMFFrequencyScheduleRule(frequency);
  if (fpRule?.dayMode === "none") return [];

  const validSchemeDays = Array.from(
    new Set(schemeDays.filter((day) => Number.isInteger(day))),
  ).sort((left, right) => left - right);

  if (!fpRule) {
    return validSchemeDays.filter((day) => day >= 1 && day <= 31);
  }

  const allowed = new Set(fpRule.allowedDays);
  const intersection = validSchemeDays.filter((day) => allowed.has(day));
  return intersection.length > 0 ? intersection : [...fpRule.allowedDays];
};

export const mfFrequencyNeedsInstallmentDay = (frequency: string) =>
  getMFFrequencyScheduleRule(frequency)?.dayMode !== "none";

export const isValidMFInstallmentDay = (
  frequency: string,
  day: number,
  schemeDays: number[] = [],
) =>
  mfFrequencyNeedsInstallmentDay(frequency) &&
  getAllowedMFInstallmentDays(frequency, schemeDays).includes(day);

export const getMFWeekdayLabel = (day: number) =>
  ["", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday"][day] ||
  `Day ${day}`;

export const formatMFInstallmentDay = (frequency: string, day: number) =>
  getMFFrequencyScheduleRule(frequency)?.dayMode === "weekday"
    ? getMFWeekdayLabel(day)
    : `Day ${day}`;

/**
 * Today's value wins when available. Otherwise, the closest supported value
 * is used; an equally close future value wins.
 */
export const getNearestAvailableScheduleDay = (
  availableDays: number[],
  today: number,
) => {
  if (availableDays.length === 0) return null;

  return availableDays.reduce((nearest, candidate) => {
    const nearestDistance = Math.abs(nearest - today);
    const candidateDistance = Math.abs(candidate - today);

    if (candidateDistance < nearestDistance) return candidate;
    if (candidateDistance > nearestDistance) return nearest;

    const candidateIsUpcoming = candidate >= today;
    const nearestIsUpcoming = nearest >= today;
    if (candidateIsUpcoming !== nearestIsUpcoming) {
      return candidateIsUpcoming ? candidate : nearest;
    }

    return candidate < nearest ? candidate : nearest;
  });
};

export const getDefaultMFInstallmentDay = (
  frequency: string,
  schemeDays: number[] = [],
  now = new Date(),
) => {
  const rule = getMFFrequencyScheduleRule(frequency);
  const allowedDays = getAllowedMFInstallmentDays(frequency, schemeDays);
  if (rule?.dayMode === "none" || allowedDays.length === 0) return null;

  const target =
    rule?.dayMode === "weekday"
      ? now.getDay() === 0
        ? 1
        : now.getDay() === 6
          ? 5
          : now.getDay()
      : now.getDate();

  return getNearestAvailableScheduleDay(allowedDays, target);
};
